<template>
  <section>
    <portal v-if="$vuetify.display.mobile && anyUnreadActivities" to="notifications-center-header-mobile-action">
      <menu-button>
        <the-button text block @click="markAllActivitiesAsRead">
          {{ $translate("ActivitiesContainer.markAllAsRead") }}
        </the-button>
      </menu-button>
    </portal>
    <template v-if="!$vuetify.display.mobile">
      <div class="d-flex justify-space-between align-center my-2 mx-4">
        <h1 class="font-size-big font-weight-bold text-left py-1">{{ $translate("common.notifications") }}</h1>
        <the-button v-if="anyUnreadActivities" text small :loading="markingAllActivitiesAsRead" @click="markAllActivitiesAsRead">
          {{ $translate("ActivitiesContainer.markAllAsRead") }}
        </the-button>
      </div>
      <v-divider class="border-post-detail-15" />
    </template>
    <loading-indicator v-if="loadingActivities" class="activities-container" :class="{ 'mb-n4 ': $vuetify.display.mobile }" />
    <pull-to-refresh
      :disabled="!$vuetify.display.mobile"
      class="pull-to-refresh-content overflow-y-auto"
      scroll-container-selector=".fill-area"
      @pull-to-refresh="fetchActivities"
    >
      <div v-if="!loadingActivities && showDisabledPushNotificationsPrompt">
        <disabled-push-notifications-prompt class="shadow-none" />
        <v-divider class="border-post-detail-15" />
      </div>
      <activity-list v-if="activities.length" :activities="activities" />
      <div v-else-if="!loadingActivities" class="activities-container d-flex flex-column justify-center px-4 font-size-medium">
        <div class="pt-1">{{ $translate("ActivitiesContainer.noNotificationsYet") }}</div>
        <div class="mt-2 pb-3">{{ $translate("ActivitiesContainer.createSomePosts") }}</div>
      </div>
    </pull-to-refresh>
  </section>
</template>

<script>
import { mapGetters, mapActions } from "pinia"

import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"
import { useConfigurationStore } from "@/store/configuration.js"

import { parseISO, differenceInWeeks } from "date-fns"

import * as activitiesApi from "@/api/activitiesApi.js"
import * as environment from "@/services/environment.js"

import ActivityList from "@/components/activities/ActivityList.vue"
import DisabledPushNotificationsPrompt from "@/components/DisabledPushNotificationsPrompt.vue"
import PullToRefresh from "@/components/PullToRefresh.vue"
import * as notificationCenterService from "@/services/notificationCenter.js"

export default {
  components: {
    ActivityList,
    PullToRefresh,
    DisabledPushNotificationsPrompt
  },
  data() {
    return {
      activities: [],
      loadingActivities: false,
      markingAllActivitiesAsRead: false,
      supportsPushNotifications: environment.supportsPushNotifications()
    }
  },
  computed: {
    ...mapGetters(useUserSessionStore, ["isLoggedIn"]),
    ...mapGetters(useConfigurationStore, ["configuration"]),
    ...mapGetters(useAppStateStore, ["disabledPushNotificationPromptSkipDate"]),
    showDisabledPushNotificationsPrompt() {
      return (
        this.supportsPushNotifications &&
        Notification.permission !== "granted" &&
        (!this.disabledPushNotificationPromptSkipDate || differenceInWeeks(new Date(), parseISO(this.disabledPushNotificationPromptSkipDate)) >= 1)
      )
    },
    anyUnreadActivities() {
      return this.activities.some((a) => !a.seen)
    }
  },
  mounted() {
    if (this.isLoggedIn) {
      this.fetchActivities()
      this.resetUnreadActivitiesCounter()
    }
  },
  methods: {
    ...mapActions(useUserSessionStore, ["resetUnreadActivitiesCounter"]),
    async markAllActivitiesAsRead() {
      try {
        this.markingAllActivitiesAsRead = true
        await activitiesApi.markAllAsRead()
        this.$eventBus.$emit("notify", {
          message: this.$translate("ActivitiesContainer.allNotificationsMarkedRead"),
          timeout: 10000,
          customCloseButton: {
            clickCallback: notificationCenterService.undoMarkAllActivitiesAsRead,
            label: this.$translate("ActivitiesContainer.undo")
          }
        })
      } catch (err) {
        this.$reportError(err)
      } finally {
        this.markingAllActivitiesAsRead = false
      }

      this.resetActivities()
    },
    resetActivities() {
      this.activities = []
      this.fetchActivities()
    },
    async fetchActivities() {
      if (this.loadingActivities) {
        return
      }
      try {
        this.loadingActivities = true
        const { activities } = await activitiesApi.activities()
        this.activities = activities
      } catch (err) {
        this.$reportError(err)
      } finally {
        this.loadingActivities = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.activities-container {
  min-height: 80px;
}
</style>
